import React, { Fragment, useEffect, useState } from 'react';
import Button from '../components/Button';
import Icon from '../components/Icon';
import { Link, RouteComponentProps } from 'react-router-dom';
import 'react-datepicker/dist/react-datepicker.css';
import { useFavorites } from '../context/FavoritesContext';
import { useData } from '../context/DataContext';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useQueryStrings } from '../context/QueryStringContext';
import Popup from '../components/Popup';
import { useGlobalControls } from '../context/GlobalControlsContext';
import { useApp } from '../context/AppContext';
import qs from 'query-string';

type NavigationProps = NavigationPropsBase & RouteComponentProps;

interface NavigationPropsBase {}

function Navigation(props: NavigationProps) {
  const { ...rest } = props;
  const { config } = useApp();

  const { updateQs } = useQueryStrings();
  const { favorites } = useFavorites();
  const { data, todayLineup } = useData();
  const { toggleOverflow } = useGlobalControls();

  const [menuVisible, setMenuVisible] = useState<'nav' | 'lang' | 'none'>('none');

  useEffect(() => {
    toggleOverflow('navigation', menuVisible !== 'none');
  }, [menuVisible]);

  function scrollToTop() {
    window.scrollTo({
      top: 0,
      behavior: 'smooth', // For a smooth scrolling animation
    });
  }

  const totalFavorites =
    Object.keys(favorites.events).length +
    Object.keys(favorites.venues).length +
    Object.keys(favorites.offers).length;

  const onBookmarkPage = location.pathname === '/bookmarks';

  if (config.hideNavigation) {
    return <></>;
  }

  return (
    <>
      <div className="sticky" style={{ height: 77 }}>
        <div className="w-full py-2 bg-white flex items-center justify-center border-b border-slate-300 nav-bar">
          {/* FIXME: not the best, but this only works on external links because of the default route */}
          <Link
            onClick={() => {
              setMenuVisible('none');
              scrollToTop();
            }}
            to={{
              pathname: config.onHomeUrl,
              search: props.location.search,
            }}
          >
            <div className="nav-logo"></div>
          </Link>
          <Link
            className="flex-grow min-w-0 leading-none text-black"
            onClick={() => {
              setMenuVisible('none');
              scrollToTop();
            }}
            to={{
              pathname: config.onHomeUrl,
              search: props.location.search,
            }}
          >
            <div className="nav-text-1 text-lg font-semibold truncate"></div>
            <div className="nav-text-2 text-lg font-semibold truncate"></div>
          </Link>
          <div className="flex justify-center items-center mr-2" style={{ height: '60px' }}>
            {data.appConfig.LANGUAGES.length > 1 && (
              <Button
                className="py-3 px-2 nav-icon"
                onClick={() => setMenuVisible(menuVisible !== 'lang' ? 'lang' : 'none')}
              >
                <Icon icon={['fass', 'language']} size="xl" />
              </Button>
            )}
            <Link
              onClick={() => setMenuVisible('none')}
              to={{ pathname: '/bookmarks', search: props.location.search }}
              className="py-3 px-2 text-black nav-icon"
            >
              <Icon
                icon={['far', 'heart']}
                size="xl"
                showBubble={totalFavorites != 0 && !onBookmarkPage}
                bubbleClassName="bg-blue-500 nav-bubble"
                bubbleText={String(totalFavorites)}
              />
            </Link>
            <Button
              onClick={() => setMenuVisible(menuVisible !== 'nav' ? 'nav' : 'none')}
              className="py-3 px-2 nav-icon"
            >
              <Icon
                icon={['fass', 'bars']}
                size="xl"
                showBubble={todayLineup.messages.length > 0}
                bubbleText={String(todayLineup.messages.length)}
                bubbleClassName="bg-blue-500 nav-bubble"
              />
            </Button>
          </div>
        </div>
      </div>
      {menuVisible === 'nav' && (
        <Popup onClose={() => setMenuVisible('none')} hideTop>
          <div className="flex flex-col justify-evenly text-lg font-semibold w-full bg-white py-1 border-b border-slate-300 float-nav-menu">
            {config.additionalNavbarLinks
              .filter(link => link.top)
              .map((link, i) => (
                <NavMenuItem
                  {...rest}
                  key={i}
                  newTab={link.newTab}
                  isExternal={link.isExternal}
                  className="py-3 px-4 text-black nav-icon"
                  name={link.label}
                  to={link.url}
                  toggleNavMenu={() => setMenuVisible('none')}
                />
              ))}
            {config.enabledViews.map((view, i) => (
              <Fragment key={i}>
                {view === 'lineup' && (
                  <NavMenuItem
                    {...rest}
                    icon={['far', 'calendar-day']}
                    className="py-3 px-4 text-black nav-icon"
                    name={data.appConfig.LINEUP_LABEL}
                    to="/lineup"
                    toggleNavMenu={() => setMenuVisible('none')}
                  />
                )}
                {view === 'explore' && (
                  <NavMenuItem
                    {...rest}
                    icon={['far', 'compass']}
                    className="py-3 px-4 text-black nav-icon"
                    name={data.appConfig.EXPLORE_LABEL}
                    to="/explore"
                    toggleNavMenu={() => setMenuVisible('none')}
                  />
                )}
                {view === 'offers' && (
                  <NavMenuItem
                    {...rest}
                    icon={['far', 'hand-holding-circle-dollar']}
                    className="py-3 px-4 text-black nav-icon"
                    name={data.appConfig.OFFERS_LABEL}
                    to="/offers"
                    toggleNavMenu={() => setMenuVisible('none')}
                  />
                )}
                {view === 'messages' && (
                  <NavMenuItem
                    {...rest}
                    icon={['far', 'message-dots']}
                    className="py-3 px-4 text-black nav-icon"
                    name={data.appConfig.MESSAGES_LABEL}
                    to="/messages"
                    toggleNavMenu={() => setMenuVisible('none')}
                    bubbleClassName="bg-blue-500 nav-bubble"
                    showBubble={todayLineup.messages.length > 0}
                    bubbleText={String(todayLineup.messages.length)}
                  />
                )}
                {view === 'information' && (
                  <NavMenuItem
                    {...rest}
                    icon={['far', 'info-circle']}
                    className="py-3 px-4 text-black nav-icon"
                    name={data.appConfig.INFORMATION_LABEL}
                    to="/information"
                    toggleNavMenu={() => setMenuVisible('none')}
                  />
                )}
              </Fragment>
            ))}
            {config.additionalNavbarLinks
              .filter(link => !link.top)
              .map((link, i) => (
                <NavMenuItem
                  {...rest}
                  key={i}
                  newTab={link.newTab}
                  isExternal={link.isExternal}
                  className="py-3 px-4 text-black nav-icon"
                  name={link.label}
                  to={link.url}
                  toggleNavMenu={() => setMenuVisible('none')}
                />
              ))}
          </div>
        </Popup>
      )}
      {menuVisible === 'lang' && (
        <Popup onClose={() => setMenuVisible('none')} hideTop>
          <div className="flex flex-col justify-evenly text-lg font-semibold w-full bg-white py-1 border-b border-slate-300 float-nav-menu">
            {data.appConfig.LANGUAGES.map(l => (
              <Button
                className="py-3 px-4 font-semibold flex items-center lang-item"
                onClick={() => {
                  updateQs('lang', l.key);
                  setMenuVisible('none');
                }}
              >
                {l.label}
              </Button>
            ))}
          </div>
        </Popup>
      )}
    </>
  );
}

interface NavMenuItemPropsBase {
  icon?: IconProp;
  name: string;
  to: string;
  newTab?: boolean;
  isExternal?: boolean;
  toggleNavMenu: () => void;
  showBubble?: boolean;
  bubbleClassName?: string;
  bubbleText?: string;
}

type NavMenuItemProps = React.HTMLAttributes<HTMLDivElement> &
  NavMenuItemPropsBase &
  RouteComponentProps;

function NavMenuItem(props: NavMenuItemProps) {
  const {
    icon,
    name,
    to,
    isExternal,
    newTab,
    toggleNavMenu,
    showBubble,
    bubbleClassName,
    bubbleText,
    className,
  } = props;

  const classes = ['flex items-center w-full'];

  if (className) {
    classes.push(className);
  }

  const [toPath, toSearch] = to.split('?');
  const qsFromLocation = qs.parse(props.location.search);
  const qsFromTo = qs.parse(toSearch);

  const finalQs = { ...qsFromLocation, ...qsFromTo };

  const WrapperComponent = ({ children }: { children: React.ReactNode }) =>
    isExternal ? (
      <a
        href={to}
        target={newTab ? '_blank' : '_self'}
        rel="noopener noreferrer"
        onClick={toggleNavMenu}
        className={classes.join(' ')}
      >
        {children}
      </a>
    ) : (
      <Link
        to={{ pathname: toPath, search: `?${qs.stringify(finalQs)}` }}
        onClick={toggleNavMenu}
        className={classes.join(' ')}
      >
        {icon && (
          <>
            <Icon
              icon={icon}
              showBubble={showBubble}
              bubbleClassName={bubbleClassName}
              bubbleText={bubbleText}
            />
            <div className="mr-2" />
          </>
        )}
        {children}
      </Link>
    );

  return (
    <WrapperComponent>
      <div>{name}</div>
    </WrapperComponent>
  );
}

export default Navigation;
